import { useEffect, useRef, useState } from "react";
import "../../../css/Checks/Check.css";
import "../../../css/Buttons/RadioButton.css";

import SecondaryButton from "../../Components/Buttons/SecondaryButton";
import Banner from "../../Components/Banners/Banner";
import ToggleSwitch from "../../Components/Buttons/ToggleSwitch";
import SubmissionPopUp from "../../Components/PopUp/SubmissionPopUp";

import { ReactComponent as PersonalInformationIcon } from "../../../assets/icons/personalInformation.svg";
import { ReactComponent as DocumentIcon } from "../../../assets/icons/identificationDocuments.svg";
import { ReactComponent as AddressIcon } from "../../../assets/icons/currentAddress.svg";
import { ReactComponent as ContactIcon } from "../../../assets/icons/contactInfo.svg";

import TextInput from "../../Components/Inputs/TextInput";
import DateInput from "../../Components/Inputs/DateInput";
import MobileInput from "../../Components/Inputs/MobileNumberInput";
import SearchInput from "../../Components/Inputs/SearchInput";

import {
  makePhoneChange,
  makeHandleChange,
  makeDobChange,
  makeRootToggle,
} from "../utils/factories/factories";

import {
  initialStandardState,
  validateStandard,
} from "../utils/checkStates/standardState";

import { isValidForm, buildPayload } from "../utils/amlForm";
import ImageUploadContainer from "../../Components/FileUplaodContainer/ImageUploadContainer";
import fileToBase64 from "../utils/fileToBase64";

import { getEndUserId } from "../utils/helperFunctions/getEndUserId";
import { auth, firestore as dbAML} from "../../../backend/firebaseConfig";
import { addDoc, collection, updateDoc, doc, setDoc } from "firebase/firestore";
import { useAdminMails as useAdminAdviserMails } from "../../../context/AdminEmailsContext";
import PrimaryButton from "../../Components/Buttons/PrimaryButton";
import { useNavigate } from "react-router-dom";
import {
  getAddressAutocomplete,
  getAddressById,
} from "../utils/postcodes/getAddressClient";
import { useCheck2GuideUrl } from "../utils/guide/getUrl";
import { useUserEmail } from "../../../context/UserEmailContext";



const StandardCheck = () => {
  const [formData, setFormData] = useState(initialStandardState);
  const [errors, setErrors] = useState({});
  const [images, setImages] = useState({ front: null, back: null });
  const [imageErrors, setImageErrors] = useState({ front: "", back: "" });
  const [resetSeed, setResetSeed] = useState(0);

  const AML_SERVER = process.env.REACT_APP_AML_SERVER;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmissionPopUp, setShowSubmissionPopUp] = useState(false);
  const navigate = useNavigate();
  const { userEmail } = useUserEmail();
  const { currentAdviserEmail } = useAdminAdviserMails();

  const handleChange = makeHandleChange(setFormData, setErrors);
  const handleRootToggle = makeRootToggle(setFormData);
  const handleDobChange = makeDobChange(setFormData, setErrors);
  const handlePhoneChange = makePhoneChange(setFormData, setErrors);

   const check2GuideUrl = useCheck2GuideUrl();
  const isDrivingLicence =
    formData.idDoc.type === "drivingLicense" ||
    formData.idDoc.type === "driving_licence";

  const handleIdDocChange = (value) => {
    setFormData((p) => ({ ...p, idDoc: { ...p.idDoc, type: value } }));

    setErrors((p) => ({
      ...p,
      idDoc: { ...(p.idDoc || {}), type: "", back: "" },
    }));
    setImageErrors({ front: "", back: "" });

    if (value === "passport") {
      setImages((p) => ({ ...p, back: null }));
    }
  };

  const onFrontImageChange = (fileOrUndefined) => {
    setImages((p) => ({ ...p, front: fileOrUndefined || null }));
    setImageErrors((p) => ({ ...p, front: "" }));
  };
  const onBackImageChange = (fileOrUndefined) => {
    setImages((p) => ({ ...p, back: fileOrUndefined || null }));
    setImageErrors((p) => ({ ...p, back: "" }));
  };

  const onFrontImageError = (msg) =>
    setImageErrors((p) => ({ ...p, front: msg || "Upload failed." }));
  const onBackImageError = (msg) =>
    setImageErrors((p) => ({ ...p, back: msg || "Upload failed." }));

  // ---------------- Address search (getAddress.io) ----------------
  const [postcodeSearch, setPostcodeSearch] = useState(
    initialStandardState?.address?.postcode || ""
  );

  // separate variable used for API calls
  const [postcodeSearchTerm, setPostcodeSearchTerm] = useState("");

  const [addrState, setAddrState] = useState({ state: "idle", msg: "" });
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [showAddrDropdown, setShowAddrDropdown] = useState(false);

  const addrDebounceRef = useRef(null);
  const autoAbortRef = useRef(null);
  const getAbortRef = useRef(null);

  const cancel = (ref) => {
    try {
      ref.current?.abort?.();
    } catch {}
  };

  // keep search input synced with form postcode (e.g. after selecting suggestion)
  useEffect(() => {
    const pc = formData.address.postcode || "";
    setPostcodeSearch(pc);
    setPostcodeSearchTerm(pc);
  }, [formData.address.postcode]);

  const runAutocomplete = async (term) => {
    const q = String(term || "").trim();

    if (q.length < 2) {
      setAddrSuggestions([]);
      setShowAddrDropdown(false);
      setAddrState({ state: "idle", msg: "" });
      return;
    }

    cancel(autoAbortRef);
    const ac = new AbortController();
    autoAbortRef.current = ac;

    setAddrState({ state: "loading", msg: "Searching addresses..." });

    const out = await getAddressAutocomplete(q, { signal: ac.signal });

    if (out?.aborted || ac.signal.aborted) return;
    if (!out.ok) {
      setAddrSuggestions([]);
      setShowAddrDropdown(false);
      setAddrState({ state: "error", msg: out.error || "Autocomplete failed" });
      return;
    }

    const list = out.suggestions || [];
    setAddrSuggestions(list);
    setShowAddrDropdown(list.length > 0);
    setAddrState({
      state: "success",
      msg: list.length ? `Found ${list.length} results` : "No results",
    });
  };

  const applyAddressFromId = async (id) => {
    cancel(getAbortRef);
    const ac = new AbortController();
    getAbortRef.current = ac;

    setAddrState({ state: "loading", msg: "Fetching address..." });

    const out = await getAddressById(id, { signal: ac.signal });

    if (!out.ok) {
      setAddrState({
        state: "error",
        msg: out.error || "Failed to fetch address",
      });
      return;
    }

    const a = out.address || {};

    handleChange("address", "buildingNumber", "text")(a.building_number || "");
    handleChange("address", "buildingName", "text")(a.building_name || "");
    handleChange("address", "street", "text")(a.thoroughfare || a.line_1 || "");
    handleChange("address", "town", "text")(a.town_or_city || "");
    handleChange("address", "county", "text")(a.county || "");

    const nextPc =
      a.postcode || formData.address.postcode || postcodeSearch || "";
    handleChange("address", "postcode", "text")(nextPc);

    setPostcodeSearch(nextPc);
    setPostcodeSearchTerm(nextPc);

    setShowAddrDropdown(false);
    setAddrSuggestions([]);
    setAddrState({ state: "success", msg: "Address selected" });
  };

  useEffect(() => {
    return () => {
      cancel(autoAbortRef);
      cancel(getAbortRef);
      if (addrDebounceRef.current) clearTimeout(addrDebounceRef.current);
    };
  }, []);

  const resetAll = () => {
    setFormData(initialStandardState);
    setErrors({});
    setImages({ front: null, back: null });
    setImageErrors({ front: "", back: "" });
    setResetSeed((n) => n + 1);

    setPostcodeSearch("");
    setPostcodeSearchTerm("");
    setAddrSuggestions([]);
    setShowAddrDropdown(false);
    setAddrState({ state: "idle", msg: "" });
  };

  // ===============================
  // FRONTEND (React) — Standard submit
  // ===============================
  const handleSubmit = async () => {
    const nextErrors = validateStandard(formData, images);
    const needsImages = !formData.contactClient;
    const imgErr = { front: "", back: "" };

    if (needsImages) {
      const isDL =
        formData.idDoc.type === "drivingLicense" ||
        formData.idDoc.type === "driving_licence";

      if (!images.front) imgErr.front = "Front image is required.";
      if (isDL && !images.back) imgErr.back = "Back image is required.";
    }

    setErrors(nextErrors);
    setImageErrors(imgErr);

    const formOk = isValidForm(nextErrors);
    const imagesOk = !(imgErr.front || imgErr.back);
    if (!formOk || !imagesOk) return;
    if (isSubmitting) return;

    const endUserId = getEndUserId(auth.currentUser?.uid);
    const base = (AML_SERVER || "").replace(/\/$/, "");
    if (!base) {
      setErrors((p) => ({ ...p, _server: "AML server URL missing" }));
      return;
    }

    const experianData = {
      person: {
        firstName: formData.personal.firstName,
        lastName: formData.personal.lastName,
        dob: formData.personal.dob,
      },
      address: {
        buildingNumber: formData.address.buildingNumber || "",
        buildingName: formData.address.buildingName || "",
        street: formData.address.street,
        town: formData.address.town,
        county: formData.address.county,
        postcode: formData.address.postcode,
      },
      documents: [],
    };
    const ownerEmail = currentAdviserEmail || userEmail || "";

    const payload = {
      ...buildPayload(formData, "Standard"),
      experianData,
      ownerEmail: ownerEmail,
      contactClient: !!formData.contactClient,
    };

    let docRef;
    let requestRef;
    let vendorData;

    try {
      setIsSubmitting(true);

      docRef = await addDoc(
        collection(dbAML, "AML Checks", userEmail, "Checks"),
        {
          checkType: "Standard",
          createdAt: new Date().toISOString(),
          createdBy: userEmail || null,
          forAdviser: currentAdviserEmail || null,
        },
      );

      vendorData = docRef.id;

      setShowSubmissionPopUp(true);

      await updateDoc(docRef, {
        vendorData,
        endUserId: endUserId || "",
        experianStatus: "processing",
        updatedAt: new Date().toISOString(),
      });

      requestRef = await addDoc(collection(docRef, "Request Data"), {
        ...payload,
        endUserId: endUserId || "",
        createdAt: new Date().toISOString(),
        documents: {
          front: images.front
            ? {
                name: images.front.name,
                type: images.front.type,
                size: images.front.size,
                context: "document-front",
                content: await fileToBase64(images.front),
              }
            : null,
          back: images.back
            ? {
                name: images.back.name,
                type: images.back.type,
                size: images.back.size,
                context: "document-back",
                content: await fileToBase64(images.back),
              }
            : null,
        },
      });

      const startRes = await fetch(`${base}/standard-check`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          data: payload,
          vendorData,
          ownerEmail: ownerEmail,
          endUserId: endUserId || "",
        }),
      });

      const startJson = await startRes.json().catch(() => ({}));

      if (!startRes.ok) {
        await updateDoc(docRef, {
          status: "failed",
          experianStatus: "failed",
          errorMessage:
            startJson?.message ||
            startJson?.error ||
            "Failed to start Standard check",
          updatedAt: new Date().toISOString(),
        });

        throw new Error(
          startJson?.message ||
            startJson?.error ||
            "Failed to start Standard check",
        );
      }

      const sessionId = startJson?.sessionId || null;
      const sessionUrl = startJson?.sessionUrl || null;

      if (sessionId) {
        await updateDoc(docRef, {
          sessionId,
          sessionUrl,
          updatedAt: new Date().toISOString(),
        });
      }

      const experianPromise = (async () => {
        const expRes = await fetch(`${base}/standard-check/experian`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            data: payload,
            vendorData,
            endUserId: endUserId || "",
          }),
        });

        if (expRes.status !== 200) {
          throw new Error(
            "Experian returned a non-200 response. The team has been notified.",
          );
        }

        const expJson = await expRes.json();

        const expRequestId =
          expJson?.expRequestId ||
          expJson?.responseHeader?.expRequestId ||
          expJson?.experian?.expRequestId ||
          null;

        const expDocRef = doc(docRef, "Experian Response", "Response");

        await setDoc(
          expDocRef,
          {
            experianStatus: expJson?.experianStatus || "completed",
            expRequestId,
            response: expJson,
            updatedAt: new Date().toISOString(),
            updatedBy: "frontend",
          },
          { merge: true },
        );

        await updateDoc(docRef, {
          experianStatus: expJson?.experianStatus || "completed",
          expRequestId,
          updatedAt: new Date().toISOString(),
        });

        return expJson;
      })();

      if (!payload.contactClient && sessionId) {
        const [frontB64, backB64] = await Promise.all([
          images.front ? fileToBase64(images.front) : null,
          images.back ? fileToBase64(images.back) : null,
        ]);

        if (requestRef && (frontB64 || backB64)) {
          await updateDoc(requestRef, {
            "documents.front.base64": frontB64 || null,
            "documents.back.base64": backB64 || null,
          });
        }

        const uploads = [
          frontB64 ? { context: "document-front", content: frontB64 } : null,
          backB64 ? { context: "document-back", content: backB64 } : null,
        ].filter(Boolean);

        for (const u of uploads) {
          const r = await fetch(
            `${base}/standard-check/${encodeURIComponent(sessionId)}/media`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({
                context: u.context,
                timestamp: new Date().toISOString(),
                content: u.content,
              }),
            },
          );
          if (!r.ok) throw new Error(await r.text());
        }

        await fetch(
          `${base}/standard-check/${encodeURIComponent(sessionId)}/submit`,
          { method: "POST", headers: { Accept: "application/json" } },
        );
      }

      experianPromise.catch(async (e) => {
        try {
          await updateDoc(docRef, {
            experianStatus: "failed",
            errorMessage: e?.message || "Experian failed",
            updatedAt: new Date().toISOString(),
          });
        } catch {}
      });

      // resetAll();
    } catch (err) {
      if (docRef) {
        try {
          await updateDoc(docRef, {
            status: "failed",
            experianStatus: "failed",
            errorMessage: err?.message || "Server error",
            updatedAt: new Date().toISOString(),
          });
        } catch {}
      }
    } finally {
      setIsSubmitting(false);
      resetAll();
      // navigate("/my-checks");
    }
  };

  return (
    <div className="Check-Container">
      {/* <Banner
        type="info"
        message="Required when you can verify the clients identification belongs to them."
      /> */}

      <div className="Check-Content">
        <ToggleSwitch
          label="Request that the client uploads their ID document."
          checked={formData.contactClient}
          onChange={handleRootToggle("contactClient")}
        />

        {/* Personal Information */}
        <div className="Check-Section">
          <div className="Check-Section-Header">
            <PersonalInformationIcon />
            <div className="AML-Text-Input primary">Personal Information</div>
          </div>

          <div className="Check-Section-Inputs">
            <TextInput
              key={`fn-${resetSeed}`}
              label="First Name"
              value={formData.personal.firstName}
              isRequired
              onChange={handleChange("personal", "firstName", "text")}
              placeholder="Alex"
              error={errors?.personal?.firstName}
            />
            <TextInput
              key={`ln-${resetSeed}`}
              label="Last Name"
              value={formData.personal.lastName}
              isRequired
              onChange={handleChange("personal", "lastName", "text")}
              placeholder="Smith"
              error={errors?.personal?.lastName}
            />
            <DateInput
              key={`dob-${resetSeed}`}
              label="Date of Birth"
              value={formData.personal.dob}
              isRequired
              onChange={handleDobChange}
              placeholderText="DD/MM/YYYY"
              dateFormat="DD/MM/YYYY"
              isError={errors?.personal?.dob}
            />
          </div>
        </div>

        <div className="Horizontal-Line" />

        {formData.contactClient === true && (
          <>
            <div className="Check-Section">
              <div className="Check-Section-Header">
                <ContactIcon />
                <div className="AML-Text-Input primary">Contact Information</div>
              </div>
              <div className="Check-Section-Inputs">
                <TextInput
                  label="Email Address"
                  value={formData.personal.email}
                  isRequired
                  onChange={handleChange("personal", "email", "email")}
                  placeholder="example@example.com"
                  error={errors?.personal?.email}
                />
                {/* <MobileInput
                  label="Phone Number"
                  value={formData.personal.phone}
                  isRequired
                  onChange={handlePhoneChange}
                  placeholder="7123 456789"
                  error={errors?.personal?.phone}
                /> */}
              </div>
            </div>
            <div className="Horizontal-Line" />
          </>
        )}

        {/* Identification Document */}
        {!formData.contactClient && (
          <div className="Check-Section">
            <div className="Check-Section-Header">
              <DocumentIcon />
              <div className="AML-Text-Input primary">Identification Document</div>
            </div>

            <Banner
              type="warning"
              message={
                <>
                  Please upload clear {isDrivingLicence ? "images" : "image"} of
                  the {isDrivingLicence ? "driving licence" : "passport"}.{" "}
                  <strong>
                    Scanned copies, printouts, or screenshots are not accepted.
                  </strong>{" "}
                  {/* <br /> */}
                  For more information please view the{" "}
                  <a
                    className="AML-Text-Input"
                    style={{
                      gap: "10px",
                      cursor: check2GuideUrl ? "pointer" : "not-allowed",
                      opacity: check2GuideUrl ? 1 : 0.5,
                    }}
                    onClick={() => {
                      if (!check2GuideUrl) return;
                      window.open(
                        check2GuideUrl,
                        "_blank",
                        "noopener,noreferrer",
                      );
                    }}
                  >
                    check² guide
                  </a>
                  .
                </>
              }
            />

            <div className="Options-Group">
              <PrimaryButton
                label="Passport"
                onClick={() => handleIdDocChange("passport")}
                isActive={formData.idDoc.type === "drivingLicense"}
              />
              <PrimaryButton
                label="Driving Licence"
                onClick={() => handleIdDocChange("drivingLicense")}
                isActive={formData.idDoc.type !== "drivingLicense"}
              />
            </div>

            <div className="Identification-Document-Container">
              <ImageUploadContainer
                key={`front-${resetSeed}`}
                id="upload-front"
                label={
                  <>
                    Front Side <span className="required-star">*</span>
                  </>
                }
                onChange={onFrontImageChange}
                onError={onFrontImageError}
                errorMessage={imageErrors.front}
              />

              <ImageUploadContainer
                key={`back-${resetSeed}`}
                id="upload-back"
                label={
                  <>
                    Back Side{" "}
                    <span className="required-star">
                      {isDrivingLicence ? "*" : ""}
                    </span>
                  </>
                }
                onChange={onBackImageChange}
                onError={onBackImageError}
                errorMessage={imageErrors.back}
              />
            </div>

            <div className="Horizontal-Line" />
          </div>
        )}

        {/* Current Address */}
        <div className="Check-Section">
          <div className="Check-Section-Header">
            <AddressIcon />
            <div className="AML-Text-Input primary">Current Address</div>
          </div>

          <Banner
            type="info"
            message="Please enter a valid UK address, including the building number or name."
          />

          {/* postcode/address search (same as Simple) */}
          <div className="aml-postcode-wrapper">
            <SearchInput
              label="Address Lookup"
              placeholder="Start typing the address or postcode..."
              value={postcodeSearch}
              onChange={(val) => {
                // store EXACT text user typed (no normalisation)
                setPostcodeSearch(val);

                // keep formData in sync (do NOT normalise)
                handleChange("address", "postcode", "text")(val);

                // separate search term used for API calls
                setPostcodeSearchTerm(val);

                // optional: debounce while typing
                if (addrDebounceRef.current)
                  clearTimeout(addrDebounceRef.current);
                addrDebounceRef.current = setTimeout(
                  () => runAutocomplete(val),
                  300,
                );
              }}
              onSubmit={() => runAutocomplete(postcodeSearchTerm)}
            />

            {/* <div className="aml-postcode-actions">
              <SecondaryButton
                label={
                  addrState.state === "loading" ? "Searching..." : "Lookup"
                }
                onClick={() => runAutocomplete(postcodeSearchTerm)}
                isDisabled={addrState.state === "loading"}
              />
            </div> */}

            {showAddrDropdown && addrSuggestions.length > 0 && (
              <div className="aml-postcode-dropdown">
                {addrSuggestions.map((sug) => (
                  <div
                    key={sug.id}
                    className="aml-postcode-option AML-Text-Body"
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      applyAddressFromId(sug.id);
                    }}
                  >
                    {sug.address}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="Check-Section-Inputs">
            <TextInput
              key={`bnum-${resetSeed}`}
              label="Building Number"
              value={formData.address.buildingNumber}
              onChange={handleChange("address", "buildingNumber", "text")}
              placeholder="123"
              isRequired={false}
              error={errors?.address?.buildingNumber}
              maxLength={10}
            />
            <TextInput
              key={`bname-${resetSeed}`}
              label="Building Name"
              value={formData.address.buildingName}
              onChange={handleChange("address", "buildingName", "text")}
              placeholder="High Tower"
              isRequired={false}
              error={errors?.address?.buildingName}
            />
            <TextInput
              key={`street-${resetSeed}`}
              label="Street"
              value={formData.address.street}
              isRequired
              onChange={handleChange("address", "street", "text")}
              placeholder="High Street"
              error={errors?.address?.street}
            />
            <TextInput
              key={`town-${resetSeed}`}
              label="Town"
              value={formData.address.town}
              isRequired
              onChange={handleChange("address", "town", "text")}
              placeholder="London"
              error={errors?.address?.town}
            />
            <TextInput
              key={`county-${resetSeed}`}
              label="County"
              value={formData.address.county}
              isRequired
              onChange={handleChange("address", "county", "text")}
              placeholder="Greater London"
              error={errors?.address?.county}
            />
            <TextInput
              key={`pc-${resetSeed}`}
              label="Postcode"
              value={formData.address.postcode}
              isRequired
              onChange={(e) => {
                // keep as-is (no normalisation)
                const typed = e.target.value;
                handleChange("address", "postcode", "text")(typed);
                setPostcodeSearch(typed);
                setPostcodeSearchTerm(typed);
              }}
              placeholder="AB1 2CD"
              error={errors?.address?.postcode}
            />
          </div>
        </div>
      </div>

      <SecondaryButton
        label={isSubmitting ? "Submitting..." : "Submit"}
        onClick={handleSubmit}
        isDisabled={isSubmitting}
      />

      {showSubmissionPopUp && (
        <SubmissionPopUp
          onClose={() => {
            setShowSubmissionPopUp(false);
            // navigate("/check²");
          }}
        />
      )}
    </div>
  );
};

export default StandardCheck;
